import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight, Sun, MapPin, ShieldCheck, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Reveal } from '@/components/motion/primitives';

export const Route = createFileRoute('/about')({
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
      <Reveal>
        <Badge variant="farm" size="lg">
          <Sun aria-hidden /> Why buy direct
        </Badge>
        <h1 className="mt-5 font-display text-hero font-black leading-[0.95] text-soil-900">
          The mango you buy
          <br />
          <span className="text-gradient-brand">was picked green.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-xl leading-relaxed text-soil-600">
          Most of them are. Then they're put in a room with calcium carbide, which releases
          acetylene, which forces the skin to turn yellow in about two days. The fruit looks ripe.
          Inside, it never finished.
        </p>
      </Reveal>

      <Reveal className="mt-14">
        <div className="prose-lg space-y-6 text-soil-700">
          <p className="leading-relaxed">
            That is not a scandal — it's just the economics. Produce passes through a commission
            agent, a wholesaler, a distributor, and a retailer before it reaches you. Each one adds
            a day and takes a cut. By the time a mango has survived that trip, it needs to have been
            picked early and coloured artificially, or it would arrive as pulp.
          </p>

          <p className="leading-relaxed">
            The farmer, meanwhile, sees a fraction of what you pay. They also have no idea who eats
            what they grew, and you have no idea who grew what you're eating. Everyone in this
            arrangement is a stranger, and the fruit is worse for it.
          </p>

          <h2 className="pt-6 font-display text-2xl font-bold text-soil-900">
            So we took out the middle
          </h2>

          <p className="leading-relaxed">
            SreeRasa buys at the farm, or lists what the farmer posts themselves, and delivers it to
            your door. That's the whole model. It means the mango can be picked at three-quarter
            maturity and ripened in hay for nine days, the way it was done before carbide existed.
            It takes longer. It tastes like the difference.
          </p>

          <h2 className="pt-6 font-display text-2xl font-bold text-soil-900">
            "Farm fresh" is a phrase. This is a place.
          </h2>

          <p className="leading-relaxed">
            Anyone can print "farm fresh" on a box. What nobody does is tell you <em>which</em>{' '}
            farm. Every listing here carries the village, the district, the farmer's name, and how
            they grow — often with photos or video from the field. A farm is verified before its
            produce goes live, and a farmer cannot publish their own listing. That last part is
            inconvenient for us and non-negotiable, because a promise you can self-certify isn't
            worth making.
          </p>

          <p className="leading-relaxed">
            One thing we deliberately don't share: the farmer's contact details. You get to know
            exactly where your food came from and who grew it. They get to keep their phone number.
          </p>
        </div>
      </Reveal>

      <Reveal className="mt-16">
        <div className="grid gap-5 sm:grid-cols-3">
          {[
            {
              icon: MapPin,
              title: 'A named place',
              body: 'Village and district on every listing. Not a region — a place.',
            },
            {
              icon: ShieldCheck,
              title: 'Verified first',
              body: 'We visit before we list. Farmers cannot approve their own produce.',
            },
            {
              icon: Users,
              title: 'Farmer sets the price',
              body: 'They grew it. We handle delivery and payment, not their margin.',
            },
          ].map((item) => (
            <div key={item.title} className="rounded-card border border-soil-200 bg-white p-6">
              <div className="flex size-10 items-center justify-center rounded-xl bg-farm-50 text-farm-600">
                <item.icon className="size-5" aria-hidden />
              </div>
              <h3 className="mt-4 font-display font-bold text-soil-900">{item.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-soil-600">{item.body}</p>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal className="mt-16">
        <div className="rounded-3xl bg-gradient-to-br from-farm-600 to-farm-800 p-10 text-center">
          <h2 className="font-display text-3xl font-black text-white">Taste the difference</h2>
          <p className="mx-auto mt-3 max-w-md text-farm-100">
            Or don't take our word for it — order once and see whether a hay-ripened mango is
            actually different. We think it is.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/shop">
              <Button size="lg" variant="harvest" className="group">
                Shop the harvest
                <ArrowRight
                  className="transition-transform group-hover:translate-x-1"
                  aria-hidden
                />
              </Button>
            </Link>
            <Link to="/farms">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 hover:text-white"
              >
                Meet the farms
              </Button>
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
